import React, { useState } from "react";
import ReactLoading from 'react-loading';
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import moment from "moment";
import BuscarPartidoResultCard from "./BuscarPartidoResultCard";

function BuscarPartidoResultListItem({ busqueda, getResultadoFromPartido, getEtapaFromPartido }) {
    const resultadoPartido = getResultadoFromPartido(busqueda);
    const aguadaPrimero = busqueda.local;
    const primerPuntaje = aguadaPrimero ? resultadoPartido.scoreAguada : resultadoPartido.scoreRival;
    const segundoPuntaje = aguadaPrimero ? resultadoPartido.scoreRival : resultadoPartido.scoreAguada;
    const fechaEsValida = moment(busqueda.fecha).isValid();

    return (
        <Link
            to={`/partido/${busqueda._id}`}
            className={`grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-3 py-3 sm:grid-cols-[6.5rem_minmax(0,1fr)_auto_auto] sm:gap-4 sm:px-4 ${busqueda.ganado ? 'hover:bg-green-500/10' : 'hover:bg-red-500/10'} transition-colors`}
        >
            <div className="text-xs sm:text-sm font-semibold text-slate-400">
                {fechaEsValida ? moment(busqueda.fecha).format('DD MMM YYYY').toLowerCase() : <span className="text-xs sm:text-sm font-extrabold leading-tight">SIN FECHA</span>}
            </div>
            <div className="min-w-0">
                <p className={`text-[10px] font-bold uppercase sm:hidden ${busqueda.ganado ? 'text-green-400' : 'text-red-400'}`}>{resultadoPartido.label}</p>
                <h3 className="truncate text-sm font-bold text-white sm:text-base">vs {busqueda.adversario}</h3>
                <p className="truncate text-[11px] text-slate-400 sm:text-xs">{busqueda.liga} · {getEtapaFromPartido(busqueda)} · {busqueda.local ? 'Local' : 'Visitante'}</p>
            </div>
            <span className={`hidden text-xs font-bold sm:block ${busqueda.ganado ? 'text-green-400' : 'text-red-400'}`}>{resultadoPartido.label}</span>
            <div className="text-right">
                <p className="whitespace-nowrap text-lg font-black text-white sm:text-xl">
                    <span className={aguadaPrimero ? (busqueda.ganado ? 'text-green-400' : 'text-red-400') : 'text-slate-300'}>{primerPuntaje}</span>
                    <span className="px-1 text-slate-500">-</span>
                    <span className={aguadaPrimero ? 'text-slate-300' : (busqueda.ganado ? 'text-green-400' : 'text-red-400')}>{segundoPuntaje}</span>
                </p>
                {busqueda.alargues?.length > 0 && <p className="text-[10px] text-slate-500">Alargue</p>}
            </div>
        </Link>
    );
}

function BuscarPartidoResults({
    jugados,
    ganados,
    perdidos,
    isLoading,
    data,
    getResultadoFromPartido,
    getEtapaFromPartido,
}) {
    const [view, setView] = useState('list');

    return (
        <section className="flex-1 flex flex-col gap-4 sm:gap-6 relative min-h-[26rem]">
            <div className="px-4">
                <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-xl border-b border-white/10 px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:gap-4">
                        <p className="text-slate-400 text-xs sm:text-sm font-medium whitespace-nowrap">
                            <span className="text-white font-bold">{jugados}</span> resultados
                        </p>
                        <div className="h-4 w-[1px] bg-white/20"></div>
                        <div className="flex items-center gap-1.5 text-xs sm:text-sm font-medium">
                            <span className="text-green-400 font-bold">{ganados}V</span>
                            <span className="text-slate-500">-</span>
                            <span className="text-red-400 font-bold">{perdidos}D</span>
                        </div>
                        <div className="ml-auto flex rounded-lg border border-white/15 p-0.5 text-xs font-bold">
                            <button
                                type="button"
                                onClick={() => setView('list')}
                                className={`relative z-10 rounded-md px-2.5 py-1.5 transition-colors duration-200 ${view === 'list' ? 'text-[#062a1a]' : 'text-slate-400 hover:text-white'}`}
                                aria-pressed={view === 'list'}
                            >
                                {view === 'list' ? (
                                    <motion.span
                                        layoutId="buscar-partido-view-toggle-pill"
                                        transition={{ type: "spring", stiffness: 420, damping: 34 }}
                                        className="absolute inset-0 -z-10 rounded-md bg-green-500"
                                    />
                                ) : null}
                                Listado
                            </button>
                            <button
                                type="button"
                                onClick={() => setView('cards')}
                                className={`relative z-10 rounded-md px-2.5 py-1.5 transition-colors duration-200 ${view === 'cards' ? 'text-[#062a1a]' : 'text-slate-400 hover:text-white'}`}
                                aria-pressed={view === 'cards'}
                            >
                                {view === 'cards' ? (
                                    <motion.span
                                        layoutId="buscar-partido-view-toggle-pill"
                                        transition={{ type: "spring", stiffness: 420, damping: 34 }}
                                        className="absolute inset-0 -z-10 rounded-md bg-green-500"
                                    />
                                ) : null}
                                Tarjetas
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-h-[100vh] overflow-y-auto pr-1 px-4">

            {isLoading &&
                <div className="absolute inset-0 z-20 flex backdrop-blur-sm bg-black/30 rounded-xl">
                    <div className="m-auto text-center">
                        <ReactLoading type="bars" color="#22c55e" width={70} className="m-auto mb-4" />
                        <p className="text-center text-base sm:text-lg font-semibold text-white">Buscando partidos...</p>
                    </div>
                </div>
            }

            <div className={view === 'cards' ? 'flex flex-col gap-3 sm:gap-4' : 'bg-white/5 backdrop-blur-xl border border-white/20 rounded-xl divide-y divide-white/10 overflow-hidden'}>
                {data.length > 0 ? data.map((busqueda) => (
                    view === 'cards' ? (
                        <BuscarPartidoResultCard
                            key={busqueda._id}
                            busqueda={busqueda}
                            getResultadoFromPartido={getResultadoFromPartido}
                            getEtapaFromPartido={getEtapaFromPartido}
                        />
                    ) : (
                        <BuscarPartidoResultListItem
                            key={busqueda._id}
                            busqueda={busqueda}
                            getResultadoFromPartido={getResultadoFromPartido}
                            getEtapaFromPartido={getEtapaFromPartido}
                        />
                    )
                )) : (
                    <div className={view === 'cards' ? 'bg-white/5 border border-white/20 rounded-xl p-6 sm:p-10 text-center text-slate-400 text-sm italic' : 'p-6 text-center text-sm italic text-slate-400 sm:p-10'}>
                        No hay partidos para mostrar
                    </div>
                )}
            </div>
            </div>
        </section>
    );
}

export default BuscarPartidoResults;
